import type { CSSProperties } from 'react';
import {
  adminButton,
  adminField,
  adminFocusable,
  adminSurface,
  adminText,
  adminTokens as t,
} from '../../admin-ui';

/**
 * Centerstage page styles over the admin tokens. The handoff's prototype
 * palette maps onto them: Source Sans for Inter, `surface.canvas` for the
 * page, `border.control` for the heavier chip and separator line,
 * `brand.roseForeground` for the selected category, links and accents, and
 * the dark admin primary for "Build a lesson" (the admin system keeps magenta
 * out of button fills).
 */

const CONTENT_MAX_WIDTH = '1240px';
const CARD_MIN_WIDTH = '258px';
const GRID_GAP = '20px';

const metaLabel: CSSProperties = {
  margin: 0,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: t.text.secondary,
};

const accentLink: CSSProperties = {
  ...adminFocusable,
  padding: 0,
  border: 'none',
  background: 'none',
  fontFamily: 'inherit',
  fontSize: t.typography.label.size,
  fontWeight: 600,
  color: t.brand.roseForeground,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
};

const statePanel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: t.spacing.sm,
  padding: `${t.spacing['2xl']} ${t.spacing.xl}`,
  textAlign: 'center',
  borderRadius: t.radius.tile,
};

export type CenterstageStyleKey =
  | 'page'
  | 'wrap'
  | 'sticky'
  | 'titleBlock'
  | 'title'
  | 'description'
  | 'findBar'
  | 'searchRow'
  | 'searchPill'
  | 'searchPillFocused'
  | 'searchInput'
  | 'clearButton'
  | 'browseGroup'
  | 'browseLink'
  | 'buildButton'
  | 'suggest'
  | 'suggestHeader'
  | 'suggestRow'
  | 'suggestRowActive'
  | 'suggestGlyph'
  | 'suggestName'
  | 'suggestCategory'
  | 'select'
  | 'selectSet'
  | 'categoryRow'
  | 'categoryButton'
  | 'categoryButtonSelected'
  | 'categorySeparator'
  | 'chipRow'
  | 'chip'
  | 'chipRemove'
  | 'showAll'
  | 'resultCount'
  | 'grid'
  | 'card'
  | 'cardHover'
  | 'poster'
  | 'posterImage'
  | 'posterFallback'
  | 'cardBody'
  | 'cardTitle'
  | 'cardDescription'
  | 'showMoreRow'
  | 'secondaryButton'
  | 'buttonDisabled'
  | 'loadingPanel'
  | 'errorPanel'
  | 'emptyPanel'
  | 'emptyTitle'
  | 'emptyBody'
  | 'dialogScroll'
  | 'player'
  | 'floatingClose'
  | 'dialogBody'
  | 'dialogTitle'
  | 'dialogMeta'
  | 'dialogDescription'
  | 'definitionGrid'
  | 'definitionItem'
  | 'metaLabel'
  | 'linkButton'
  | 'conceptLinks'
  | 'conceptDot'
  | 'dialogFooter'
  | 'footerError'
  | 'dialogHeader'
  | 'iconButton'
  | 'conceptGroups'
  | 'conceptGroup'
  | 'conceptGroupHeader'
  | 'conceptGroupName'
  | 'conceptGroupCount'
  | 'conceptColumns'
  | 'conceptButton'
  | 'conceptButtonDisabled'
  | 'countsNote'
;

export type CenterstageStyles = Record<CenterstageStyleKey, CSSProperties>;

export function createCenterstageStyles({ narrow }: { narrow: boolean }): CenterstageStyles {
  const gutter = narrow ? t.layout.pageGutterNarrow : t.layout.pageGutter;
  const pageTop = narrow ? t.spacing.lg : t.spacing.xl;

  return {
    page: {
      ...adminSurface.page,
      padding: `${pageTop} ${gutter}`,
      minWidth: 0,
      fontFamily: t.typography.fontFamily,
    },
    wrap: {
      width: '100%',
      maxWidth: CONTENT_MAX_WIDTH,
      // Left-aligned like the other admin pages, so the title does not jump between pages on wide screens.
      margin: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.lg,
      minWidth: 0,
    },
    /** Heading, find bar, categories and chips stay put while the grid scrolls under them; the block bleeds into the gutters so card shadows do not show at its edges. */
    sticky: {
      position: 'sticky',
      top: 0,
      zIndex: 10,
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.md,
      marginTop: `calc(-1 * ${pageTop})`,
      marginLeft: `calc(-1 * ${gutter})`,
      marginRight: `calc(-1 * ${gutter})`,
      padding: `${pageTop} ${gutter} ${t.spacing.md}`,
      backgroundColor: t.surface.canvas,
    },
    titleBlock: { display: 'flex', flexDirection: 'column', gap: t.spacing.xs },
    title: adminText.pageTitle,
    description: adminText.secondary,

    // Find bar
    findBar: {
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.md,
      flexWrap: 'wrap',
    },
    searchRow: {
      position: 'relative',
      flex: '1 1 360px',
      minWidth: 0,
    },
    searchPill: {
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.sm,
      height: '44px',
      padding: `0 ${t.spacing.md} 0 ${t.spacing.lg}`,
      borderRadius: t.radius.pill,
      border: `1px solid ${t.border.control}`,
      backgroundColor: t.surface.panel,
      boxShadow: t.shadow.panel,
      boxSizing: 'border-box',
      color: t.text.secondary,
    },
    // Each state sets the whole `border` shorthand: adding and removing a longhand
    // (borderColor) next to a shorthand leaves stale borders behind in React.
    searchPillFocused: {
      border: `1px solid ${t.focus.ring}`,
      boxShadow: `0 0 0 3px color-mix(in srgb, ${t.focus.ring} 12%, transparent)`,
    },
    searchInput: {
      // The pill shows focus (searchPillFocused); an outline here would draw a second ring inside it.
      outline: 'none',
      flex: 1,
      minWidth: 0,
      height: '100%',
      border: 'none',
      background: 'transparent',
      fontFamily: 'inherit',
      fontSize: t.typography.body.size,
      color: t.text.primary,
    },
    clearButton: {
      ...adminFocusable,
      width: '28px',
      height: '28px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      padding: 0,
      border: 'none',
      borderRadius: t.radius.pill,
      background: 'none',
      color: t.text.secondary,
      cursor: 'pointer',
    },
    // Matches the "Ex:" example in the Author with AI ask field: grey "or", then an underlined rose link.
    browseGroup: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: t.spacing.sm,
      flexShrink: 0,
      color: t.text.secondary,
      fontSize: '14px',
      whiteSpace: 'nowrap',
    },
    browseLink: {
      ...adminFocusable,
      padding: 0,
      border: 'none',
      background: 'none',
      fontFamily: 'inherit',
      fontSize: '14px',
      color: t.brand.roseForeground,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    suggest: {
      position: 'absolute',
      top: 'calc(100% + 6px)',
      left: 0,
      right: 0,
      zIndex: 20,
      margin: 0,
      padding: `${t.spacing.xs} 0`,
      listStyle: 'none',
      backgroundColor: t.surface.panel,
      border: `1px solid ${t.border.subtle}`,
      borderRadius: t.radius.tile,
      boxShadow: t.shadow.overlay,
    },
    suggestHeader: { ...metaLabel, padding: `${t.spacing.sm} ${t.spacing.lg} ${t.spacing.xs}` },
    suggestRow: {
      display: 'flex',
      alignItems: 'center',
      gap: t.spacing.sm,
      padding: `${t.spacing.sm} ${t.spacing.lg}`,
      fontSize: t.typography.body.size,
      color: t.text.primary,
      cursor: 'pointer',
    },
    suggestRowActive: { backgroundColor: t.navigation.selected },
    suggestGlyph: { color: t.text.secondary, fontSize: '12px', flexShrink: 0 },
    suggestName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    suggestCategory: {
      marginLeft: 'auto',
      paddingLeft: t.spacing.md,
      flexShrink: 0,
      fontSize: t.typography.label.size,
      color: t.text.secondary,
    },
    select: {
      ...adminField.select,
      height: '44px',
      padding: `0 ${t.spacing.md} 0 ${t.spacing.lg}`,
      borderRadius: t.radius.pill,
      flex: '0 0 auto',
    },
    selectSet: { fontWeight: 600, border: `1px solid ${t.text.primary}` },

    // Category row
    categoryRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: t.spacing.sm,
      overflowX: 'auto',
      whiteSpace: 'nowrap',
      scrollbarWidth: 'thin',
      scrollbarColor: `${t.border.control} transparent`,
      paddingBottom: '2px',
    },
    categoryButton: {
      ...adminFocusable,
      flexShrink: 0,
      padding: `6px 2px`,
      border: 'none',
      borderBottom: '2px solid transparent',
      background: 'none',
      fontFamily: 'inherit',
      fontSize: t.typography.body.size,
      fontWeight: 500,
      color: t.text.secondary,
      cursor: 'pointer',
    },
    categoryButtonSelected: {
      fontWeight: 600,
      color: t.text.primary,
      borderBottom: `2px solid ${t.brand.roseForeground}`,
    },
    categorySeparator: {
      flexShrink: 0,
      width: '1px',
      height: '14px',
      backgroundColor: t.border.control,
    },

    // Chips
    chipRow: {
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '6px',
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    chip: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: t.spacing.xs,
      height: '28px',
      padding: `0 ${t.spacing.xs} 0 ${t.spacing.md}`,
      borderRadius: t.radius.pill,
      border: `1px solid ${t.border.control}`,
      backgroundColor: t.surface.panel,
      fontSize: t.typography.label.size,
      color: t.text.primary,
      boxSizing: 'border-box',
    },
    chipRemove: {
      ...adminFocusable,
      width: '22px',
      height: '22px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      border: 'none',
      borderRadius: t.radius.pill,
      background: 'none',
      color: t.text.secondary,
      cursor: 'pointer',
    },
    showAll: { ...accentLink, marginLeft: 'auto' },

    // Grid
    resultCount: {
      margin: 0,
      fontSize: t.typography.label.size,
      color: t.text.secondary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, minmax(min(${CARD_MIN_WIDTH}, 100%), 1fr))`,
      gap: GRID_GAP,
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    card: {
      ...adminFocusable,
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: 0,
      textAlign: 'left',
      fontFamily: 'inherit',
      color: t.text.primary,
      backgroundColor: t.surface.panel,
      border: `1px solid ${t.border.subtle}`,
      borderRadius: t.radius.tile,
      boxShadow: t.shadow.panel,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'transform 120ms ease, box-shadow 120ms ease',
    },
    cardHover: { transform: 'translateY(-2px)', boxShadow: t.shadow.overlay },
    poster: {
      position: 'relative',
      display: 'block',
      width: '100%',
      aspectRatio: '16 / 9',
      backgroundColor: t.surface.subtle,
      overflow: 'hidden',
    },
    posterImage: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
    posterFallback: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: t.text.secondary,
      fontSize: '24px',
    },
    cardBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.xs,
      padding: `${t.spacing.md} ${t.spacing.lg} ${t.spacing.lg}`,
    },
    cardTitle: { display: 'block', margin: 0, fontSize: '14px', fontWeight: 600, lineHeight: 1.35, color: t.text.primary },
    cardDescription: {
      margin: 0,
      fontSize: t.typography.label.size,
      lineHeight: t.typography.label.lineHeight,
      color: t.text.secondary,
      display: '-webkit-box',
      WebkitLineClamp: 3,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
    showMoreRow: { display: 'flex', justifyContent: 'center', paddingTop: t.spacing.sm },
    secondaryButton: adminButton.secondary,
    buttonDisabled: adminButton.disabled,
    loadingPanel: { ...statePanel, color: t.text.secondary, fontSize: t.typography.body.size },
    errorPanel: { ...statePanel, ...adminSurface.panel },
    emptyPanel: { ...statePanel, border: `1px dashed ${t.border.control}` },
    emptyTitle: { ...adminText.body, fontWeight: 600 },
    emptyBody: adminText.secondary,

    // Dialogs
    dialogScroll: { flex: 1, minHeight: 0, overflowY: 'auto', color: t.text.primary, fontFamily: t.typography.fontFamily },
    // Takes focus when the player drops it (see CenterstageVideoModal); the ring sits inside the video edge.
    player: { position: 'relative', width: '100%', aspectRatio: '16 / 9', backgroundColor: '#000', outlineColor: t.focus.ring, outlineOffset: '-3px' },
    floatingClose: {
      ...adminFocusable,
      position: 'absolute',
      top: t.spacing.md,
      right: t.spacing.md,
      zIndex: 2,
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      border: 'none',
      borderRadius: t.radius.pill,
      backgroundColor: 'rgba(255, 255, 255, 0.92)',
      color: t.text.primary,
      cursor: 'pointer',
      boxShadow: t.shadow.panel,
    },
    dialogBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: t.spacing.md,
      padding: `${t.spacing.lg} ${t.spacing.xl} ${t.spacing.xl}`,
    },
    dialogTitle: adminText.sectionTitle,
    dialogMeta: { margin: 0, fontSize: t.typography.label.size, color: t.text.secondary },
    dialogDescription: adminText.body,
    definitionGrid: {
      display: 'grid',
      gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
      gap: t.spacing.md,
      margin: 0,
    },
    definitionItem: { display: 'flex', flexDirection: 'column', gap: t.spacing.xs, margin: 0 },
    metaLabel,
    linkButton: { ...accentLink, fontSize: t.typography.body.size, textAlign: 'left', whiteSpace: 'normal' },
    conceptLinks: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      gap: `${t.spacing.xs} ${t.spacing.sm}`,
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    conceptDot: { color: t.text.secondary },
    dialogFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flexWrap: 'wrap',
      gap: t.spacing.md,
      padding: `${t.spacing.lg} ${t.spacing.xl}`,
      borderTop: `1px solid ${t.border.subtle}`,
      fontFamily: t.typography.fontFamily,
    },
    footerError: { margin: 0, marginRight: 'auto', flex: '1 1 240px' },
    buildButton: { ...adminButton.primary, display: 'inline-flex', alignItems: 'center', gap: t.spacing.sm },
    dialogHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: t.spacing.md,
      padding: `${t.spacing.lg} ${t.spacing.xl}`,
      borderBottom: `1px solid ${t.border.subtle}`,
      fontFamily: t.typography.fontFamily,
    },
    iconButton: adminButton.icon,
    conceptGroups: { display: 'flex', flexDirection: 'column', gap: t.spacing.xl, padding: `${t.spacing.lg} ${t.spacing.xl} ${t.spacing.xl}` },
    conceptGroup: { display: 'flex', flexDirection: 'column', gap: t.spacing.sm, margin: 0 },
    conceptGroupHeader: { display: 'flex', alignItems: 'baseline', gap: t.spacing.sm },
    conceptGroupName: { margin: 0, fontSize: '15px', fontWeight: 700, color: t.text.primary },
    conceptGroupCount: { fontSize: t.typography.label.size, color: t.text.secondary },
    conceptColumns: {
      display: 'grid',
      gridTemplateColumns: narrow ? '1fr' : 'repeat(3, minmax(0, 1fr))',
      gap: `${t.spacing.xs} ${t.spacing.lg}`,
      margin: 0,
      padding: 0,
      listStyle: 'none',
    },
    conceptButton: {
      ...adminFocusable,
      width: '100%',
      padding: `${t.spacing.xs} 0`,
      border: 'none',
      background: 'none',
      textAlign: 'left',
      fontFamily: 'inherit',
      fontSize: t.typography.body.size,
      color: t.text.primary,
      cursor: 'pointer',
    },
    conceptButtonDisabled: { color: t.text.disabled, cursor: 'default' },
    countsNote: { margin: 0, fontSize: t.typography.label.size, color: t.text.secondary },
  };
}
